
import { Plane, Gift, Sparkles, Navigation, Calendar, Bell, UserCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";

interface EmptyStateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EmptyStateDialog = ({ open, onOpenChange }: EmptyStateDialogProps) => {
  const { t } = useTranslation();
  
  // Temporarily disable the dialog by forcing open to false
  open = false; // This line forces the dialog to stay closed
  
  const features = [
    {
      icon: <Plane className="w-6 h-6 text-travel-500" />,
      title: t("index.welcome.features.tripManagement.title"),
      description: t("index.welcome.features.tripManagement.description"),
    },
    {
      icon: <Sparkles className="w-6 h-6 text-purple-500" />,
      title: t("index.welcome.features.inspiration.title"),
      description: t("index.welcome.features.inspiration.description"),
    },
    {
      icon: <Gift className="w-6 h-6 text-pink-500" />,
      title: t("index.welcome.features.offers.title"),
      description: t("index.welcome.features.offers.description"),
    },
  ];

  const tips = [
    {
      icon: <UserCircle2 className="w-5 h-5 text-indigo-500" />,
      text: t("index.welcome.tips.profile"),
    },
    {
      icon: <Navigation className="w-5 h-5 text-blue-500" />,
      text: t("index.welcome.tips.addTrip"),
    },
    {
      icon: <Calendar className="w-5 h-5 text-green-500" />,
      text: t("index.welcome.tips.dates"),
    },
    {
      icon: <Bell className="w-5 h-5 text-amber-500" />,
      text: t("index.welcome.tips.notifications"),
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full h-[calc(100vh-12rem)] overflow-y-auto bg-white/95 backdrop-blur-sm">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center mb-6">
            {t("index.welcome.title")}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="p-4 rounded-lg bg-gradient-to-br from-white to-gray-50 shadow-md hover:shadow-lg transition-shadow"
              >
                <div className="flex flex-col items-center text-center space-y-3">
                  <div className="p-3 rounded-full bg-white shadow-inner">
                    {feature.icon}
                  </div>
                  <h3 className="font-semibold text-lg">{feature.title}</h3>
                  <p className="text-sm text-gray-600">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-travel-50/50 rounded-lg p-6">
            <h3 className="font-semibold text-lg mb-4 text-center">Quick Tips to Get Started</h3>
            <div className="space-y-4">
              {tips.map((tip, index) => (
                <div key={index} className="flex items-center space-x-3 bg-white p-3 rounded-md shadow-sm">
                  <div className="flex-shrink-0">{tip.icon}</div>
                  <p className="text-sm text-gray-700">{tip.text}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center text-sm text-gray-600 pt-4">
            <p>{t("index.welcome.ready")}</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
